import axios from 'axios';
import { logger } from '@parrot-software/observability';

import type { ITaxPort } from '@onboarding/application/ports';
import type { TaxDto } from '@onboarding/application/dtos';
import { createTaxConfig } from '@onboarding/domain/value-objects';

const onbLog = logger.forDomain('ONB');

/**
 * Use case for applying the tax configuration during onboarding.
 *
 * Flow: TaxDto → domain value object → persist default IVA tax (Parrot API).
 * The infrastructure layer resolves the concrete IVA tax UUID by matching the
 * requested percent value against the store's pre-populated IVA tax group.
 *
 * @param props.taxPort - ITaxPort implementation (injected)
 * @returns Use case executor
 */
export const applyTaxUseCase = (props: { taxPort: ITaxPort }) => {
  const { taxPort } = props;

  const execute = async (dto: TaxDto): Promise<void> => {
    try {
      const config = createTaxConfig(dto.rate);
      await taxPort.applyTaxConfig(config);
    } catch (error) {
      onbLog.error('tax_apply_failed', {
        'error.kind': (error as { constructor?: { name?: string } })?.constructor?.name ?? 'UnknownError',
        'error.message': error instanceof Error ? error.message : String(error),
        'http.status_code': axios.isAxiosError(error) ? error.response?.status : undefined,
        'onb.tax_rate': dto.rate,
      });
      throw error;
    }
  };

  return { execute };
};
